/**
 * 응답 봉투(envelope) 어댑터 — 서버마다 성공·실패 JSON 모양이 달라 서비스별로 고른다.
 * 응답 모양을 추측해 언래핑하지 않는다(`'data' in json` 추측은 data 필드를 가진 일반 응답까지 벗겨 낸다).
 */
export interface ErrorDetail {
  code?: string;
  message?: string;
  requestId?: string;
}

export interface Envelope {
  name: string;
  /** 성공 본문이 이 봉투 모양인지 — 아니면 invalid-response 에러로 실패시킨다. */
  accepts: (body: unknown) => boolean;
  /** 성공 본문 → 화면이 쓸 값 */
  unwrap: (body: unknown) => unknown;
  /** 실패 본문 → 코드·메시지 */
  readError: (body: unknown) => ErrorDetail;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown) => (typeof value === 'string' && value !== '' ? value : undefined);

/** FastAPI 기본 에러 — `{detail: "..."}` 또는 422 `{detail: [{msg}]}` */
function readFastApiDetail(body: unknown): ErrorDetail {
  if (!isRecord(body)) {
    return {};
  }
  const { detail } = body;
  if (typeof detail === 'string') {
    return { message: detail };
  }
  if (Array.isArray(detail)) {
    const messages = detail.flatMap((item) => (isRecord(item) && str(item.msg) ? [item.msg] : []));
    return { code: 'VALIDATION_ERROR', message: messages.join(' / ') || undefined };
  }
  return {};
}

/** MGRV 공통 봉투 `{code, message, data}` — mgrv-api-java(MG200), app-mesher(SUCCESS), web-mesher("200") */
export const mgrvEnvelope: Envelope = {
  name: 'mgrv',
  // data가 null인 성공 응답도 있으므로 키 존재만 본다.
  accepts: (body) => isRecord(body) && 'data' in body,
  unwrap: (body) => (isRecord(body) ? body.data : body),
  readError: (body) => {
    if (isRecord(body) && ('code' in body || 'message' in body)) {
      // 일부 서버는 메시지 앞에 "[NHN-…]" 같은 내부 접두어를 붙인다.
      return { code: str(body.code), message: str(body.message)?.replace(/^\[[^\]]+\]\s*/, '') };
    }
    return readFastApiDetail(body);
  },
};

/** 신규 코어 API 봉투 — 성공 `{data}`, 실패 `{error: {code, message, request_id}}` */
export const dataErrorEnvelope: Envelope = {
  name: 'data-error',
  accepts: (body) => isRecord(body) && 'data' in body,
  unwrap: (body) => (isRecord(body) ? body.data : body),
  readError: (body) => {
    if (isRecord(body) && isRecord(body.error)) {
      const { error } = body;
      return {
        code: str(error.code),
        message: str(error.message),
        requestId: str(error.request_id),
      };
    }
    return readFastApiDetail(body);
  },
};

/** 봉투 없는 서버(인증 서버 등) — 본문 그대로 */
export const rawEnvelope: Envelope = {
  name: 'raw',
  accepts: () => true,
  unwrap: (body) => body,
  readError: readFastApiDetail,
};
